import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  useFetchCustomerSuppliedSinks,
  useFetchInStoreSinks,
  useFetchSinks,
} from './useFetchSinks';

import { useSelectCutoutLabor } from '~/api/easy-estimator-api/cutout-labor-api/hooks/useCutoutLabor';
import { useSelectedMaterial } from '~/api/easy-estimator-api/materials-api/hooks/useSelectedMaterial';
import { SectionTypes } from '~/core/enums/section-type-enum';
import { useRetailerSettings } from '~/core/hooks/useRetailerSettings';
import { ApplianceModel, CutoutLaborModel } from '~/core/models';
import { isCustomerSuppliedSink } from '~/core/utils';
import { useDrawingShapeContext } from '~/legacy/easy-estimator-page/context';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import {
  decreaseSinkQuantity,
  selectSelectedSinkProductId,
  selectSelectedSinks,
  updateInStoreSinksUrl,
} from '~/redux/slices/sink';

interface UseSinkReturn {
  browsedSinks: ApplianceModel[];
  customerSuppliedSinks: ApplianceModel[];
  decreaseSinkQuantityHandler: (sinkProductId: number) => () => void;
  getSinkCutoutLaborProductData: (sink: ApplianceModel) => {
    cutoutLaborProduct: ApplianceModel | CutoutLaborModel;
    cutoutLaborProductName: string;
  };
  isFetchingSinks: boolean;
  selectedSinkId: number | undefined;
  selectedSinks: ReturnType<typeof selectSelectedSinks>;
  setShowRemoveApplianceModal: (show: boolean) => void;
  showRemoveApplianceModal: boolean;
}

export const useSink = (): UseSinkReturn => {
  const dispatch = useAppDispatch();

  const { getInStoreApplianceUrl } = useRetailerSettings();

  const { selectSinkCutout } = useSelectCutoutLabor();

  const selectedMaterial = useSelectedMaterial();

  const {
    getters: { selectedSinksInDrawing },
  } = useDrawingShapeContext();

  const { programSinks, isFetchingSinks } = useFetchSinks();
  const { inStoreSinks, isFetchingInStoreSinks } = useFetchInStoreSinks();
  const { customerSuppliedSinks, isFetchingCustomerSuppliedSinks } =
    useFetchCustomerSuppliedSinks();

  const browsedSinks: ApplianceModel[] = useMemo(
    () => programSinks.concat(inStoreSinks),
    [programSinks, inStoreSinks]
  );

  const getSinkCutoutLaborProductData = useCallback(
    (sink: ApplianceModel) => {
      // Look up the cutout labor product for this sink from /products/cutout-labor API
      // This gives us the correct cutout labor name for all sinks (browsed and customer-supplied)
      const selectedSinkCutout = selectSinkCutout({
        materialType: selectedMaterial?.productLine,
        sink,
      });
      const cutoutLaborProduct = isCustomerSuppliedSink(sink)
        ? sink
        : (selectedSinkCutout ?? sink);

      return {
        cutoutLaborProduct,
        cutoutLaborProductName: cutoutLaborProduct.name ?? '',
      };
    },
    [selectSinkCutout, selectedMaterial?.productLine]
  );

  const selectedSinkId = useAppSelector(selectSelectedSinkProductId);

  /** Selected sink only NOT in drawing */
  const selectedSinksInPanelOnly = useAppSelector(selectSelectedSinks);

  const selectedSinks: Record<number, number> = useMemo(
    () =>
      selectedSinksInDrawing.reduce(
        (acc, { productId, quantity }) => {
          acc[productId] = (acc[productId] || 0) + quantity;

          return acc;
        },
        { ...selectedSinksInPanelOnly }
      ),
    [selectedSinksInPanelOnly, selectedSinksInDrawing]
  );

  const [showRemoveApplianceModal, setShowRemoveApplianceModal] =
    useState(false);

  const decreaseSinkQuantityHandler = (sinkProductId: number) => () => {
    if (selectedSinksInPanelOnly[sinkProductId]) {
      dispatch(decreaseSinkQuantity(sinkProductId));
    } else {
      setShowRemoveApplianceModal(true);
    }
  };

  useEffect(() => {
    dispatch(updateInStoreSinksUrl(getInStoreApplianceUrl(SectionTypes.sink)));
  }, [dispatch, getInStoreApplianceUrl]);

  return {
    browsedSinks,
    customerSuppliedSinks,
    decreaseSinkQuantityHandler,
    getSinkCutoutLaborProductData,
    isFetchingSinks:
      isFetchingInStoreSinks ||
      isFetchingSinks ||
      isFetchingCustomerSuppliedSinks,
    selectedSinkId,
    selectedSinks,
    setShowRemoveApplianceModal,
    showRemoveApplianceModal,
  };
};
